<template>
  <div
    class="offcanvas offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      <span class="fw-semibold">
        <i class="fa-solid fa-kitchen-set me-2 text-warning" />
        {{ type === 'CREATE'? $t('Create Station'): $t('Edit Station') }}
      </span>
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>
      <div class="px-3">
        <div class="row g-3 py-3">
          <!-- Branch -->
          <div class="col-12">
            <label class="form-label">
              {{ $t('Branch') }} <span class="text-danger">*</span>
            </label>
            <select2
              style="width: 100%;"
              v-model="v$.form.branch_id.$model"
              :options="select2Branches.options"
              :settings="select2Branches.setting"
              :placeholder="$t('Select Branch') + '...'"
              :disabled="type === 'EDIT'"
            />
            <div class="form-text" v-if="type === 'EDIT'">
              {{ $t('Branch cannot be changed.') }}
            </div>
            <div v-for="error of v$.form.branch_id.$errors"
              class="d-block invalid-feedback"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>

          <!-- Name -->
          <div class="col-12">
            <label class="form-label">
              {{ $t('Station Name') }} <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              v-model.trim="v$.form.name.$model"
              class="form-control shadow-none"
              :placeholder="$t('e.g. KDS 1 - Main Kitchen')"
            >
            <div v-for="error of v$.form.name.$errors"
              class="d-block invalid-feedback"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>

          <!-- Color picker -->
          <div class="col-12">
            <label class="form-label d-block">{{ $t('Station Color') }}</label>
            <div class="d-flex flex-wrap gap-2">
              <button
                v-for="c in colorPresets"
                :key="c"
                type="button"
                class="kds-swatch"
                :class="{ 'kds-swatch--active': form.color === c }"
                :style="{ backgroundColor: c }"
                @click="form.color = c"
              >
                <i v-if="form.color === c" class="fa-solid fa-check" />
              </button>
            </div>
          </div>

          <!-- Active -->
          <div class="col-12">
            <div class="form-check form-switch">
              <input
                id="kdsStationActive"
                class="form-check-input"
                type="checkbox"
                role="switch"
                v-model="form.is_active"
              >
              <label class="form-check-label" for="kdsStationActive">
                {{ form.is_active ? $t('Active') : $t('Inactive') }}
              </label>
            </div>
          </div>

          <!-- Categories (multi-select) -->
          <div class="col-12">
            <label class="form-label d-flex justify-content-between">
              <span>{{ $t('Categories') }} <span class="text-danger">*</span></span>
              <span class="text-muted small">{{ $t('Selected') }}: {{ form.category_ids.length }}</span>
            </label>
            <div class="border rounded p-2" style="max-height: 260px; overflow-y: auto;">
              <div v-if="!categoryOptions.length" class="text-muted small text-center py-3">
                {{ $t('No Available Data') }}
              </div>
              <label
                v-for="cat in categoryOptions"
                :key="cat.id"
                class="d-flex align-items-center p-2 rounded kds-cat-row"
                :class="{ 'kds-cat-row--checked': form.category_ids.includes(cat.id) }"
              >
                <input
                  type="checkbox"
                  class="form-check-input me-2 mt-0"
                  :value="cat.id"
                  v-model="form.category_ids"
                >
                {{ $ln(cat) }}
              </label>
            </div>
            <div v-for="error of v$.form.category_ids.$errors"
              class="d-block invalid-feedback"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { KdsStations } from '@/services/endpoint/kds-stations.service';
import Select2 from 'vue3-select2-component';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      colorPresets: ['#2563eb', '#16a34a', '#dc2626', '#9333ea', '#ea580c', '#0891b2', '#ca8a04', '#475569'],
      select2Branches: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
          allowClear: true
        }
      },
      categoryOptions: [],
      loading: true,
      hasPermission: false,
      loadingSave: false,
      form: {
        branch_id: null,
        name: '',
        color: '#2563eb',
        is_active: true,
        category_ids: []
      }
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    AccessDenied,
    Select2
  },
  methods: {
    mapBranches(list) {
      let vm = this;
      return (list || []).map(item => ({ id: item.id, text: vm.$ln(item) }));
    },
    async getCreateData() {
      await KdsStations.getCreate().then(res => {
        this.select2Branches.options = this.mapBranches(res.data.filter.branches);
        this.categoryOptions = res.data.filter.categories || [];
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    async getUpdateData() {
      await KdsStations.getUpdate(this.id).then(res => {
        this.select2Branches.options = this.mapBranches(res.data.filter.branches);
        this.categoryOptions = res.data.filter.categories || [];
        this.form = {
          branch_id: res.data.data.branch_id,
          name: res.data.data.name,
          color: res.data.data.color || '#2563eb',
          is_active: !!res.data.data.is_active,
          category_ids: res.data.data.category_ids || []
        };
        this.hasPermission = true;
      }).catch(err => {
        throw err;
      });
    },
    setParamsBeforeSendCreate() {
      return {
        branch_id: this.form.branch_id,
        name: this.form.name,
        color: this.form.color,
        is_active: this.form.is_active ? 1 : 0,
        category_ids: this.form.category_ids
      };
    },
    setParamsBeforeSendUpdate() {
      // branch_id เปลี่ยนไม่ได้ — ไม่ส่งไป (BE validateRequest จะ reject key ที่ไม่อนุญาต)
      return {
        name: this.form.name,
        color: this.form.color,
        is_active: this.form.is_active ? 1 : 0,
        category_ids: this.form.category_ids
      };
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await KdsStations.create(this.setParamsBeforeSendCreate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Station!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
        this.loadingSave = false;
      });
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await KdsStations.update(this.id, this.setParamsBeforeSendUpdate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Station!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  }
}
</script>

<style scoped>
.kds-swatch {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid transparent;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  transition: transform 0.12s ease;
}
.kds-swatch:hover {
  transform: scale(1.1);
}
.kds-swatch--active {
  border-color: #1f2937;
  box-shadow: 0 0 0 3px rgba(31, 41, 55, 0.18);
}
.kds-cat-row {
  cursor: pointer;
}
.kds-cat-row:hover {
  background-color: #f5f7fa;
}
.kds-cat-row--checked {
  background-color: #e7eefb;
}
</style>
